import React, {Fragment} from "react";
import moment from "moment";
import PropTypes from "prop-types";
import {
  Card,
  CardBody,
  CardHeader,
  CardSubtitle,
  CardText,
  UncontrolledTooltip,
} from "reactstrap";
import {FaCog, FaCopy, FaHandshake, FaTrash} from "react-icons/fa";
import {useDispatch, useSelector} from "react-redux";
import {cloneSnapshot, deleteDashboard} from "../../redux/Actions/tsdb.actions";
import {getUserProfile} from "../../redux/Selectors/users.selector";
import DeleteModal from "../Modal/DeleteModal";
import AddDashboard from "../Modal/AddDashboard";
import Tooltip from "../Tooltip/Tooltip";
import SnapshotHandoff from "../Modal/SnapshotHandoff";
import "./dashboardCard.scss";
import {useDisclosure} from "../../hooks/useDisclosure";

function DashboardCard(props) {
  const {
    dashboard = {},
    isEditable = false,
    onCardClick,
    type = "",
    updateCallback = () => {},
  } = props;
  const {description = "", id, name, updated_at, user_email = ""} = dashboard;
  const userProfile = useSelector(getUserProfile);
  const isStaffOrSuperuser = Boolean(userProfile?.is_staff || userProfile?.is_superuser);

  const dispatch = useDispatch();
  const {isOpen: isEditOpen, onOpen: openEdit, onClose: closeEdit} = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onOpen: openDelete,
    onClose: closeDelete,
  } = useDisclosure();
  const {
    isOpen: isHandoffOpen,
    onOpen: openHandoff,
    onClose: closeHandoff,
  } = useDisclosure();

  const cloneDashboard = () => {
    dispatch(cloneSnapshot(id, () => updateCallback()));
  };

  const handleDelete = () => {
    const callback = () => {
      closeDelete();
      updateCallback();
    };
    dispatch(deleteDashboard(id, callback));
  };

  return (
    <Fragment>
      {isDeleteOpen && (
        <DeleteModal
          header={`Delete ${name}`}
          modalBody={`Are you sure you want to delete ${name} ?`}
          onClose={closeDelete}
          onDelete={handleDelete}
          open={isDeleteOpen}
        />
      )}
      {isHandoffOpen && (
        <SnapshotHandoff
          open={isHandoffOpen}
          onCancel={closeHandoff}
          selectedSnapshot={id}
          updateCallback={updateCallback}
        />
      )}
      {isEditOpen && (
        <AddDashboard
          data={dashboard}
          headerName={name}
          handleClose={closeEdit}
          open={isEditOpen}
          updateCallback={updateCallback}
        />
      )}
      <div>
        <Card style={{position: "relative"}} className="mb-4 dashboard--card--main">
          {isEditable && (
            <Fragment>
              <FaTrash
                className="actionIcons"
                data-cy="delete_icon"
                onClick={openDelete}
                style={{
                  position: "absolute",
                  top: "15px",
                  right: "5px",
                }}
                id={`delete-${id}`}
              />
              <UncontrolledTooltip target={`delete-${id}`}>Delete</UncontrolledTooltip>
              {type === "Snapshot" && (
                <Fragment>
                  <FaHandshake
                    className="actionIcons"
                    data-cy="snapshot-handoff"
                    onClick={openHandoff}
                    style={{
                      position: "absolute",
                      top: "15px",
                      right: "35px",
                    }}
                    id={`handoff-${id}`}
                  />
                  <UncontrolledTooltip target={`handoff-${id}`}>
                    Handoff
                  </UncontrolledTooltip>
                </Fragment>
              )}
              <FaCopy
                className="actionIcons"
                data-cy="dashboard-clone"
                onClick={cloneDashboard}
                style={{
                  position: "absolute",
                  top: "15px",
                  right: "65px",
                }}
                id={`clone-${id}`}
              />
              <UncontrolledTooltip target={`clone-${id}`}>Clone</UncontrolledTooltip>
              <FaCog
                className="actionIcons"
                data-cy="edit_icon"
                onClick={openEdit}
                style={{
                  position: "absolute",
                  top: "15px",
                  right: "95px",
                }}
                id={`edit-${id}`}
              />
              <UncontrolledTooltip target={`edit-${id}`}>Edit</UncontrolledTooltip>
            </Fragment>
          )}
          <CardHeader id={`header-${id}`} className="max-title">
            {name}
          </CardHeader>
          <Tooltip target={`header-${id}`} />

          <CardBody
            onClick={() => onCardClick(dashboard)}
            style={{cursor: "pointer"}}
            data-cy={"dashboard_card"}
          >
            <CardSubtitle id={`description-${id}`} className="max-description">
              {description}
            </CardSubtitle>
            <Tooltip target={`description-${id}`} overflowType={"height"} />
            <CardText id={`update-${id}`} className="max-update">
              Last Updated: {moment.utc(updated_at).fromNow()}
            </CardText>
            {user_email && isStaffOrSuperuser && (
              <CardText id={`user-email-${id}`} className="max-description">
                Assigned to: {user_email}
              </CardText>
            )}
            <Tooltip target={`update-${id}`} />
          </CardBody>
        </Card>
      </div>
    </Fragment>
  );
}

DashboardCard.propTypes = {
  /** This is a name of page which will be used when doing edit and delete of dashboard */
  type: PropTypes.string,
  dashboard: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    meta: PropTypes.shape({updatedAt: PropTypes.instanceOf(Date)}),
  }).isRequired,
  isEditable: PropTypes.bool,
  /** This callback will be executed after edit, delete and handoff of dashboard */
  updateCallback: PropTypes.func,
  /** Callback which will be executed when clicked on card */
  onCardClick: PropTypes.func,
};

export default DashboardCard;
